import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import BulletHole from "@/components/BulletHole";
import useResizableColumns from "@/lib/useResizableColumns";
import {
  Gift,
  ShieldCheck,
  TrendingUp,
  Users,
  Search,
  Download,
  ArrowUpDown,
  X,
  Trash2,
  KeyRound,
  UserCog,
  Ban,
  CalendarClock,
  Trophy,
  Receipt,
  Copy,
  Check,
} from "lucide-react";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const shortDate = (iso) => (iso ? iso.slice(0, 10) : "—");

const STATUS_FILTERS = [
  { v: "all", label: "All" },
  { v: "trial", label: "Trial" },
  { v: "active", label: "Active" },
  { v: "canceled", label: "Canceled" },
  { v: "admin", label: "Admin" },
];

const SORTS = [
  { v: "created_at", label: "Joined" },
  { v: "email", label: "Email" },
  { v: "subscription_status", label: "Status" },
  { v: "active_referrals", label: "Refs" },
  { v: "discount_per_month_cents", label: "Discount" },
];


function OperationsPanel() {
  const [rev, setRev] = useState(null);
  const [refs, setRefs] = useState(null);
  const [flags, setFlags] = useState([]);
  const [fkey, setFkey] = useState("");

  const loadFlags = () => api.get("/admin/flags").then((r) => setFlags(r.data));
  useEffect(() => {
    api.get("/admin/revenue").then((r) => setRev(r.data)).catch(() => {});
    api.get("/admin/referral-stats").then((r) => setRefs(r.data)).catch(() => {});
    loadFlags();
  }, []);

  const addFlag = async () => {
    if (!fkey.trim()) return;
    await api.post("/admin/flags", { key: fkey, enabled: false });
    setFkey("");
    loadFlags();
  };
  const toggleFlag = async (f) => {
    await api.post("/admin/flags", { key: f.key, enabled: !f.enabled, description: f.description || "" });
    loadFlags();
  };
  const delFlag = async (f) => {
    await api.delete(`/admin/flags/${f.key}`);
    loadFlags();
  };

  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-operations">
      <div className="hud-frame p-5" data-testid="admin-revenue">
        <div className="kpi-label flex items-center gap-2"><TrendingUp size={11} /> Revenue analytics</div>
        {rev ? (
          <div className="mt-3 space-y-2">
            <div className="flex justify-between text-sm"><span>MRR</span><span className="mono text-hud">{dollars(rev.mrr_cents)}</span></div>
            <div className="flex justify-between text-sm text-muted-foreground"><span>· Pro</span><span className="mono">{dollars(rev.mrr_by_plan_cents.pro)}</span></div>
            <div className="flex justify-between text-sm text-muted-foreground"><span>· Team</span><span className="mono">{dollars(rev.mrr_by_plan_cents.team)}</span></div>
            <div className="flex justify-between text-sm"><span>Paid transactions</span><span className="mono">{rev.paid_transactions}</span></div>
            {rev.revenue_by_month.map((m) => (
              <div key={m.month} className="flex justify-between text-xs text-muted-foreground mono">
                <span>{m.month}</span><span>{dollars(m.cents)}</span>
              </div>
            ))}
          </div>
        ) : <div className="mono text-xs text-muted-foreground mt-3">Loading…</div>}
      </div>

      <div className="hud-frame p-5" data-testid="admin-referral-stats">
        <div className="kpi-label flex items-center gap-2"><Trophy size={11} /> Referral statistics</div>
        {refs ? (
          <div className="mt-3 space-y-2">
            <div className="flex justify-between text-sm"><span>Referred signups</span><span className="mono">{refs.total_referred_signups}</span></div>
            <div className="flex justify-between text-sm"><span>Paid conversions</span><span className="mono">{refs.paid_conversions}</span></div>
            <div className="flex justify-between text-sm"><span>Free years granted</span><span className="mono text-hud">{refs.free_years_granted}</span></div>
            <div className="flex justify-between text-sm"><span>Active free years</span><span className="mono">{refs.active_free_years}</span></div>
            {refs.top_referrers.slice(0, 5).map((t) => (
              <div key={t.email} className="flex justify-between text-xs text-muted-foreground mono">
                <span className="truncate">{t.email}</span><span>{t.conversions} conv · {t.rewards}yr</span>
              </div>
            ))}
          </div>
        ) : <div className="mono text-xs text-muted-foreground mt-3">Loading…</div>}
      </div>

      <div className="hud-frame p-5" data-testid="admin-flags">
        <div className="kpi-label flex items-center gap-2"><ShieldCheck size={11} /> Feature flags</div>
        <div className="flex gap-2 mt-3">
          <input value={fkey} onChange={(e) => setFkey(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFlag()}
            placeholder="new_flag_key" className="hud-input flex-1 !py-1 mono !text-xs" data-testid="flag-key-input" />
          <button onClick={addFlag} className="hud-btn" data-testid="flag-add">ADD</button>
        </div>
        <div className="mt-3 space-y-2">
          {flags.length === 0 && <div className="mono text-xs text-muted-foreground">No flags yet.</div>}
          {flags.map((f) => (
            <div key={f.key} className="flex items-center gap-2" data-testid={`flag-${f.key}`}>
              <code className="mono text-xs flex-1 truncate">{f.key}</code>
              <button onClick={() => toggleFlag(f)}
                className={`mono text-[9px] uppercase tracking-widest border px-2 py-0.5 ${f.enabled ? "text-hud border-hud" : "text-muted-foreground border-border"}`}
                data-testid={`flag-toggle-${f.key}`}>
                {f.enabled ? "ON" : "OFF"}
              </button>
              <button onClick={() => delFlag(f)} className="text-muted-foreground hover:text-[hsl(var(--signal))]" data-testid={`flag-del-${f.key}`}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatusChip({ status, role }) {
  if (role === "admin")
    return (
      <span className="hud-chip" style={{ borderColor: "hsl(var(--signal-amber))", color: "hsl(var(--signal-amber))" }}>
        Admin
      </span>
    );
  if (status === "active") return <span className="hud-chip hud-chip-on">Active</span>;
  if (status === "canceled")
    return <span className="hud-chip" style={{ borderColor: "hsl(var(--oxblood))", color: "hsl(var(--oxblood))" }}>Canceled</span>;
  return <span className="hud-chip">{(status || "trial").toUpperCase()}</span>;
}

function SignupsSpark({ series }) {
  if (!series || series.length === 0) return null;
  const w = 360;
  const h = 90;
  const pad = 6;
  const max = Math.max(1, ...series.map((s) => s.signups));
  const bw = (w - pad * 2) / series.length;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" aria-hidden>
      <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="hsl(var(--border))" strokeWidth="1" />
      {series.map((s, i) => {
        const bh = (s.signups / max) * (h - pad * 2);
        const x = pad + i * bw;
        const y = h - pad - bh;
        return (
          <g key={s.date}>
            <rect
              x={x + 1}
              y={y}
              width={Math.max(1, bw - 2)}
              height={Math.max(1, bh)}
              fill="hsl(var(--oxblood))"
              opacity={0.85}
            >
              <title>{`${s.date}: ${s.signups} signup${s.signups === 1 ? "" : "s"}`}</title>
            </rect>
            {s.active > 0 && (
              <rect
                x={x + 1}
                y={y}
                width={Math.max(1, bw - 2)}
                height={Math.max(1, (s.active / max) * (h - pad * 2))}
                fill="hsl(var(--signal-cyan))"
                opacity={0.9}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="mono text-xs">—</span>;
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {}
      }}
      className="hud-btn !px-2 !py-1 mono text-[10px]"
      title="Copy"
      data-testid="copy-chip"
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      <span>{value}</span>
    </button>
  );
}

export default function Admin() {
  const { user: actor, refresh: refreshMe } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chart, setChart] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [payments, setPayments] = useState(null);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("desc");

  const [openUid, setOpenUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newPw, setNewPw] = useState(null);
  const [showPayments, setShowPayments] = useState(false);

  // Excel-style resizable columns for the users + payments tables
  const usersCols = useResizableColumns("admin-users-v1", 9, { minWidth: 60 });
  const payCols = useResizableColumns("admin-payments-v1", 6, { minWidth: 60 });

  const loadUsers = useCallback(async () => {
    const r = await api.get("/admin/users", {
      params: { q: q || undefined, status, sort, dir },
    });
    setData(r.data);
  }, [q, status, sort, dir]);

  useEffect(() => {
    const t = setTimeout(loadUsers, 200);
    return () => clearTimeout(t);
  }, [loadUsers]);

  useEffect(() => {
    (async () => {
      const [a, b] = await Promise.all([
        api.get("/admin/signups-chart?days=30"),
        api.get("/admin/top-referrers"),
      ]);
      setChart(a.data);
      setLeaders(b.data.leaderboard || []);
    })();
  }, []);

  const openDrawer = async (uid) => {
    setOpenUid(uid);
    setDetail(null);
    setConfirmDelete(false);
    setNewPw(null);
    const r = await api.get(`/admin/users/${uid}`);
    setDetail(r.data);
  };

  const closeDrawer = () => {
    setOpenUid(null);
    setDetail(null);
  };

  const setBusyFor = (key, val) => setBusy((b) => ({ ...b, [key]: val }));

  const doGrant = async (uid) => {
    setBusyFor(`g-${uid}`, true);
    try {
      await api.post(`/admin/users/${uid}/grant?days=30`);
      await loadUsers();
      if (openUid === uid) await openDrawer(uid);
    } finally {
      setBusyFor(`g-${uid}`, false);
    }
  };

  const doCancel = async (uid) => {
    setBusyFor("cancel", true);
    try {
      await api.post(`/admin/users/${uid}/cancel`);
      await loadUsers();
      await openDrawer(uid);
    } finally {
      setBusyFor("cancel", false);
    }
  };

  const doResetPw = async (uid) => {
    setBusyFor("pw", true);
    try {
      const r = await api.post(`/admin/users/${uid}/reset-password`, {});
      setNewPw(r.data.new_password);
    } finally {
      setBusyFor("pw", false);
    }
  };

  const doImpersonate = async (uid) => {
    setBusyFor("imp", true);
    try {
      await api.post(`/admin/users/${uid}/impersonate`);
      await refreshMe();
      navigate("/");
    } finally {
      setBusyFor("imp", false);
    }
  };

  const doDelete = async (uid) => {
    setBusyFor("del", true);
    try {
      await api.delete(`/admin/users/${uid}`);
      closeDrawer();
      await loadUsers();
    } finally {
      setBusyFor("del", false);
    }
  };

  const loadPayments = async () => {
    setShowPayments(true);
    if (payments) return;
    const r = await api.get("/admin/payments?limit=100");
    setPayments(r.data);
  };

  const downloadCsv = () => {
    window.open(`${API}/admin/users.csv`, "_blank");
  };

  const headerSort = (field) => {
    if (sort === field) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(field);
      setDir("desc");
    }
  };

  const sortIndicator = (field) =>
    sort === field ? (
      <span className="text-hud ml-1">{dir === "asc" ? "↑" : "↓"}</span>
    ) : null;

  if (!data) return <div className="mono text-muted-foreground text-xs uppercase tracking-widest">Loading roster…</div>;

  const baseCents = data.base_price_cents;

  return (
    <div className="space-y-8" data-testid="admin-page">
      <header className="relative">
        <BulletHole variant={2} size={90} className="absolute -top-4 right-4 opacity-50" />
        <div className="kpi-label flex items-center gap-2">
          <ShieldCheck size={11} /> Reseller Console
        </div>
        <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
          Customer Roster
        </h1>
        <p className="mono text-xs text-muted-foreground tracking-widest mt-3 uppercase">
          Manage every Command Center subscriber from one panel.
        </p>
      </header>

      {/* KPI cards */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="hud-frame p-5">
          <div className="kpi-label flex items-center gap-2"><Users size={11} /> Customers</div>
          <div className="kpi-value mt-3">{data.counts.total}</div>
        </div>
        <div className="hud-frame p-5">
          <div className="kpi-label">Free</div>
          <div className="kpi-value mt-3">{data.counts.free}</div>
        </div>
        <div className="hud-frame p-5">
          <div className="kpi-label">Pro</div>
          <div className="kpi-value mt-3 text-[hsl(var(--signal-cyan))]">{data.counts.pro}</div>
        </div>
        <div className="hud-frame p-5">
          <div className="kpi-label">Team</div>
          <div className="kpi-value mt-3 text-hud">{data.counts.team}</div>
        </div>
        <div className="hud-frame p-5">
          <div className="kpi-label flex items-center gap-2"><TrendingUp size={11} /> MRR</div>
          <div className="kpi-value mt-3 text-hud">{dollars(data.mrr_cents)}</div>
        </div>
      </section>

      <OperationsPanel />

      {/* Chart + Leaderboard */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="hud-frame p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div className="kpi-label flex items-center gap-2">
              <CalendarClock size={11} /> Signups · last 30 days
            </div>
            <div className="mono text-xs text-muted-foreground">
              {chart ? `${chart.total} total` : "—"}
            </div>
          </div>
          {chart ? <SignupsSpark series={chart.series} /> : <div className="h-24" />}
          <div className="flex items-center gap-4 mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2" style={{ background: "hsl(var(--oxblood))" }} /> Signup</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-2 h-2" style={{ background: "hsl(var(--signal-cyan))" }} /> Activated</span>
          </div>
        </div>

        <div className="hud-frame p-5">
          <div className="kpi-label flex items-center gap-2 mb-3">
            <Trophy size={11} /> Top Referrers
          </div>
          {leaders.length === 0 ? (
            <div className="mono text-xs text-muted-foreground">No referrals yet.</div>
          ) : (
            <ol className="space-y-2">
              {leaders.map((l, i) => (
                <li
                  key={l.id}
                  className="flex items-center justify-between gap-2 cursor-pointer hover:bg-[hsl(var(--accent))]/20 -mx-2 px-2 py-1"
                  onClick={() => openDrawer(l.id)}
                  data-testid={`leader-${l.id}`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="mono text-xs text-hud w-5">#{i + 1}</span>
                    <div className="min-w-0">
                      <div className="text-xs truncate">{l.name || l.email}</div>
                      <div className="mono text-[10px] text-muted-foreground truncate">{l.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num text-sm font-semibold">{l.active_referrals}</div>
                    <div className="mono text-[9px] text-muted-foreground uppercase tracking-widest">
                      {dollars(l.earned_discount_cents)}/mo
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* Search / filter / actions */}
      <section className="flex flex-wrap items-center gap-3">
        <div className="hud-frame flex items-center gap-2 px-3 py-2 flex-1 min-w-[240px]">
          <Search size={13} className="text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, referral code…"
            className="bg-transparent outline-none flex-1 text-sm"
            data-testid="admin-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-muted-foreground hover:text-foreground">
              <X size={13} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 hud-frame p-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setStatus(f.v)}
              className={`px-3 py-1.5 mono text-[10px] uppercase tracking-widest transition-colors ${
                status === f.v
                  ? "bg-[hsl(var(--oxblood))] text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`filter-${f.v}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={downloadCsv} className="hud-btn" data-testid="export-csv">
          <Download size={11} /> Export CSV
        </button>
        <button onClick={loadPayments} className="hud-btn" data-testid="show-payments">
          <Receipt size={11} /> Payments
        </button>
      </section>

      {/* Roster table */}
      <section className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
            <thead>
              <tr>
                <th ref={usersCols.setRef(0)} style={usersCols.widthStyle(0, "160px")}>
                  <button onClick={() => headerSort("name")} className="inline-flex items-center gap-1 uppercase">
                    Name <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("name")}
                  </button>
                  <usersCols.Grip index={0} />
                </th>
                <th ref={usersCols.setRef(1)} style={usersCols.widthStyle(1, "220px")}>
                  <button onClick={() => headerSort("email")} className="inline-flex items-center gap-1 uppercase">
                    Email <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("email")}
                  </button>
                  <usersCols.Grip index={1} />
                </th>
                <th ref={usersCols.setRef(2)} style={usersCols.widthStyle(2, "120px")}>
                  <button onClick={() => headerSort("created_at")} className="inline-flex items-center gap-1 uppercase">
                    Joined <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("created_at")}
                  </button>
                  <usersCols.Grip index={2} />
                </th>
                <th ref={usersCols.setRef(3)} style={usersCols.widthStyle(3, "120px")}>
                  <button onClick={() => headerSort("subscription_status")} className="inline-flex items-center gap-1 uppercase">
                    Status <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("subscription_status")}
                  </button>
                  <usersCols.Grip index={3} />
                </th>
                <th ref={usersCols.setRef(4)} style={usersCols.widthStyle(4, "100px")}>Code<usersCols.Grip index={4} /></th>
                <th ref={usersCols.setRef(5)} style={usersCols.widthStyle(5, "80px")} className="!text-right">
                  <button onClick={() => headerSort("active_referrals")} className="inline-flex items-center gap-1 uppercase">
                    Refs <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("active_referrals")}
                  </button>
                  <usersCols.Grip index={5} />
                </th>
                <th ref={usersCols.setRef(6)} style={usersCols.widthStyle(6, "110px")} className="!text-right">
                  <button onClick={() => headerSort("discount_per_month_cents")} className="inline-flex items-center gap-1 uppercase">
                    Discount <ArrowUpDown size={9} className="opacity-50" />{sortIndicator("discount_per_month_cents")}
                  </button>
                  <usersCols.Grip index={6} />
                </th>
                <th ref={usersCols.setRef(7)} style={usersCols.widthStyle(7, "100px")} className="!text-right">Net / mo<usersCols.Grip index={7} /></th>
                <th ref={usersCols.setRef(8)} style={usersCols.widthStyle(8, "100px")}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => {
                const net = u.role === "admin" ? 0 : Math.max(0, baseCents - (u.discount_per_month_cents || 0));
                return (
                  <tr
                    key={u.id}
                    data-testid={`admin-row-${u.id}`}
                    onClick={() => openDrawer(u.id)}
                    className="cursor-pointer hover:bg-[hsl(var(--accent))]/15"
                  >
                    <td>{u.name || (u.role === "admin" ? "Admin" : "—")}</td>
                    <td className="mono text-xs">{u.email}</td>
                    <td className="mono text-xs">{shortDate(u.created_at)}</td>
                    <td><StatusChip status={u.subscription_status} role={u.role} /></td>
                    <td className="mono text-xs">{u.referral_code || "—"}</td>
                    <td className="num">{u.active_referrals || 0}</td>
                    <td className="num text-hud">{dollars(u.discount_per_month_cents || 0)}</td>
                    <td className="num font-semibold">{u.role === "admin" ? "—" : dollars(net)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {u.role !== "admin" && (
                        <button
                          onClick={() => doGrant(u.id)}
                          disabled={busy[`g-${u.id}`]}
                          className="hud-btn !px-2 !py-1"
                          data-testid={`admin-grant-${u.id}`}
                        >
                          {busy[`g-${u.id}`] ? "…" : "GRANT 30D"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {data.users.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center mono text-xs text-muted-foreground py-8">
                    No customers match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Payments ledger */}
      {showPayments && (
        <section className="hud-frame p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="kpi-label flex items-center gap-2"><Receipt size={11} /> Payments ledger</div>
            <div className="flex items-center gap-3">
              {payments && (
                <div className="mono text-xs text-muted-foreground">
                  Paid: <span className="text-hud">{dollars(payments.paid_total_cents)}</span> · {payments.count} rows
                </div>
              )}
              <button onClick={() => setShowPayments(false)} className="hud-btn !px-2 !py-1">
                <X size={11} />
              </button>
            </div>
          </div>
          {!payments ? (
            <div className="mono text-xs text-muted-foreground">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="ox-table" style={{ tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
                <thead>
                  <tr>
                    <th ref={payCols.setRef(0)} style={payCols.widthStyle(0, "140px")}>When<payCols.Grip index={0} /></th>
                    <th ref={payCols.setRef(1)} style={payCols.widthStyle(1, "180px")}>Customer<payCols.Grip index={1} /></th>
                    <th ref={payCols.setRef(2)} style={payCols.widthStyle(2, "140px")}>Purpose<payCols.Grip index={2} /></th>
                    <th ref={payCols.setRef(3)} style={payCols.widthStyle(3, "110px")}>Amount<payCols.Grip index={3} /></th>
                    <th ref={payCols.setRef(4)} style={payCols.widthStyle(4, "100px")}>Status<payCols.Grip index={4} /></th>
                    <th ref={payCols.setRef(5)} style={payCols.widthStyle(5, "200px")}>Session</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.payments.length === 0 ? (
                    <tr><td colSpan={6} className="text-center mono text-xs text-muted-foreground py-6">No payments yet.</td></tr>
                  ) : payments.payments.map((p) => (
                    <tr key={p.id || p.session_id}>
                      <td className="mono text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="mono text-xs">{p.user_email || "—"}</td>
                      <td className="mono text-xs">{p.purpose || "—"}</td>
                      <td className="num font-semibold">{dollars(p.amount_cents)}</td>
                      <td>
                        {p.payment_status === "paid" ? (
                          <span className="hud-chip hud-chip-on">PAID</span>
                        ) : (
                          <span className="hud-chip">{(p.payment_status || "unpaid").toUpperCase()}</span>
                        )}
                      </td>
                      <td className="mono text-[10px] text-muted-foreground truncate">{p.session_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Customer drawer */}
      {openUid && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={closeDrawer}
          data-testid="admin-drawer"
        >
          <div
            className="w-full sm:w-[520px] h-full bg-background border-l border-border overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tread-plate h-1.5" />
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="min-w-0">
                  <div className="kpi-label">Customer</div>
                  {detail ? (
                    <>
                      <h2 className="display text-2xl font-bold uppercase tracking-tight mt-1 leading-tight truncate">
                        {detail.user.name || detail.user.email.split("@")[0]}
                      </h2>
                      <div className="mono text-xs text-muted-foreground mt-1 truncate">{detail.user.email}</div>
                    </>
                  ) : (
                    <div className="mono text-xs text-muted-foreground mt-3">Loading…</div>
                  )}
                </div>
                <button onClick={closeDrawer} className="hud-btn !px-2 !py-1" data-testid="drawer-close">
                  <X size={13} />
                </button>
              </div>

              {detail && (
                <>
                  {/* Profile grid */}
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Status</div>
                      <div className="mt-2"><StatusChip status={detail.user.subscription_status} role={detail.user.role} /></div>
                    </div>
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Net / mo</div>
                      <div className="kpi-value mt-2 text-hud" style={{ fontSize: "1.4rem" }}>
                        {detail.user.role === "admin" ? "—" : dollars(detail.net_per_month_cents)}
                      </div>
                    </div>
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Joined</div>
                      <div className="mono text-xs mt-2">{shortDate(detail.user.created_at)}</div>
                    </div>
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Period ends</div>
                      <div className="mono text-xs mt-2">{shortDate(detail.user.current_period_end) || "—"}</div>
                    </div>
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Referral code</div>
                      <div className="mt-2">
                        <CopyChip value={detail.user.referral_code} />
                      </div>
                    </div>
                    <div className="hud-frame p-3">
                      <div className="kpi-label">Referred by</div>
                      <div className="mono text-xs mt-2 truncate">
                        {detail.referrer ? detail.referrer.email : "—"}
                      </div>
                    </div>
                  </div>

                  <div className="kpi-label mb-2">Plan &amp; credits</div>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <select
                      value={detail.user.plan || "free"}
                      onChange={async (e) => {
                        await api.post(`/admin/users/${detail.user.id}/set-plan`, { plan: e.target.value });
                        openDrawer(detail.user.id);
                        loadUsers();
                      }}
                      className="hud-input !py-1 mono !text-xs uppercase"
                      data-testid="drawer-set-plan"
                      disabled={detail.user.role === "admin"}
                    >
                      <option value="free">FREE</option>
                      <option value="pro">PRO</option>
                      <option value="team">TEAM</option>
                    </select>
                    <button
                      onClick={async () => {
                        await api.post(`/admin/users/${detail.user.id}/credit`, { months: 12 });
                        openDrawer(detail.user.id);
                      }}
                      className="hud-btn justify-center"
                      data-testid="drawer-credit"
                    >
                      <Gift size={11} /> Credit 12 months
                    </button>
                  </div>

                  {/* Action buttons */}
                  <div className="kpi-label mb-2">Actions</div>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    <button
                      onClick={() => doGrant(detail.user.id)}
                      disabled={busy[`g-${detail.user.id}`] || detail.user.role === "admin"}
                      className="hud-btn hud-btn-primary justify-center"
                      data-testid="drawer-grant"
                    >
                      <CalendarClock size={11} />
                      Grant 30 Days
                    </button>
                    <button
                      onClick={() => doCancel(detail.user.id)}
                      disabled={busy.cancel || detail.user.role === "admin" || detail.user.subscription_status === "canceled"}
                      className="hud-btn justify-center"
                      data-testid="drawer-cancel"
                    >
                      <Ban size={11} />
                      Cancel Sub
                    </button>
                    <button
                      onClick={() => doResetPw(detail.user.id)}
                      disabled={busy.pw}
                      className="hud-btn justify-center"
                      data-testid="drawer-reset-pw"
                    >
                      <KeyRound size={11} />
                      {busy.pw ? "Resetting…" : "Reset Password"}
                    </button>
                    <button
                      onClick={() => doImpersonate(detail.user.id)}
                      disabled={busy.imp || detail.user.id === actor?.id}
                      className="hud-btn justify-center"
                      data-testid="drawer-impersonate"
                    >
                      <UserCog size={11} />
                      Sign in as user
                    </button>
                    <button
                      onClick={() => setConfirmDelete(true)}
                      disabled={detail.user.role === "admin"}
                      className="hud-btn justify-center col-span-2"
                      style={{ borderColor: "hsl(var(--oxblood))", color: "hsl(var(--oxblood))" }}
                      data-testid="drawer-delete"
                    >
                      <Trash2 size={11} />
                      Delete Account…
                    </button>
                  </div>

                  {newPw && (
                    <div className="hud-frame p-4 mb-5" style={{ borderColor: "hsl(var(--signal-amber))" }}>
                      <div className="kpi-label" style={{ color: "hsl(var(--signal-amber))" }}>New password (share with user)</div>
                      <div className="flex items-center gap-2 mt-2">
                        <code className="mono text-sm flex-1 bg-[hsl(var(--accent))]/30 px-2 py-1">{newPw}</code>
                        <button
                          onClick={async () => {
                            try { await navigator.clipboard.writeText(newPw); } catch {}
                          }}
                          className="hud-btn !px-2 !py-1"
                          data-testid="copy-new-pw"
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    </div>
                  )}

                  {confirmDelete && (
                    <div className="hud-frame p-4 mb-5" style={{ borderColor: "hsl(var(--oxblood))" }}>
                      <div className="kpi-label" style={{ color: "hsl(var(--oxblood))" }}>Confirm delete</div>
                      <p className="text-sm mt-2">
                        Permanently remove <span className="mono">{detail.user.email}</span> and all their data. This cannot be undone.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => doDelete(detail.user.id)}
                          disabled={busy.del}
                          className="hud-btn hud-btn-primary"
                          data-testid="drawer-delete-confirm"
                        >
                          {busy.del ? "Deleting…" : "Yes, delete forever"}
                        </button>
                        <button onClick={() => setConfirmDelete(false)} className="hud-btn">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Referrals */}
                  <div className="kpi-label mb-2 flex items-center gap-2">
                    <Trophy size={11} /> Referrals brought ({detail.referrals.length})
                  </div>
                  {detail.referrals.length === 0 ? (
                    <div className="mono text-xs text-muted-foreground mb-4">No referrals yet.</div>
                  ) : (
                    <ul className="space-y-1 mb-5">
                      {detail.referrals.map((r) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between text-xs hud-frame px-3 py-2 cursor-pointer hover:bg-[hsl(var(--accent))]/20"
                          onClick={() => openDrawer(r.id)}
                        >
                          <div className="min-w-0">
                            <div className="truncate">{r.name || r.email}</div>
                            <div className="mono text-[10px] text-muted-foreground truncate">{r.email}</div>
                          </div>
                          <StatusChip status={r.subscription_status} />
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Recent payments */}
                  <div className="kpi-label mb-2 flex items-center gap-2">
                    <Receipt size={11} /> Recent payments ({detail.payments.length})
                  </div>
                  {detail.payments.length === 0 ? (
                    <div className="mono text-xs text-muted-foreground">No payments on file.</div>
                  ) : (
                    <ul className="space-y-1">
                      {detail.payments.slice(0, 5).map((p) => (
                        <li key={p.session_id || p.id} className="flex items-center justify-between text-xs hud-frame px-3 py-2">
                          <span className="mono text-[10px] text-muted-foreground">{(p.created_at || "").slice(0, 10)}</span>
                          <span className="num font-semibold">{dollars(p.amount_cents)}</span>
                          <span>
                            {p.payment_status === "paid"
                              ? <span className="hud-chip hud-chip-on !text-[9px]">PAID</span>
                              : <span className="hud-chip !text-[9px]">{(p.payment_status || "unpaid").toUpperCase()}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
